// src/pages/Production/SubcontractorProductionReport.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    backLink: {
        textDecoration: 'none',
        color: '#06C167',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '30px'
    },
    summaryCards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '30px'
    },
    summaryCard: {
        background: '#f8fafc',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #e2e8f0'
    },
    cardTitle: {
        fontSize: '13px',
        color: '#64748b',
        marginBottom: '6px'
    },
    cardValue: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #e2e8f0'
    },
    th: {
        background: '#f8fafc',
        padding: '14px 16px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase'
    },
    td: {
        padding: '14px 16px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    statusBadge: {
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: '600'
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b'
    }
};

function SubcontractorProductionReport() {
    const { userData } = useAuth();
    const [productionOrders, setProductionOrders] = useState([]);
    const [subcontractors, setSubcontractors] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        const unsubOrders = onSnapshot(
            query(collection(db, 'companies', companyId, 'productionOrders'), orderBy('createdAt', 'desc')),
            snap => setProductionOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        const unsubSubcontractors = onSnapshot(
            query(collection(db, 'companies', companyId, 'suppliers')),
            snap => {
                setSubcontractors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                setLoading(false);
            }
        );

        return () => {
            unsubOrders();
            unsubSubcontractors();
        };
    }, [userData]);

    const reportData = useMemo(() => {
        const subcontractorData = subcontractors.map(sub => {
            // Find orders assigned to this subcontractor
            const assignedOrders = productionOrders.filter(order =>
                order.stages?.some(stage =>
                    stage.subcontractors?.some(s => s.supplierId === sub.id)
                ) || order.assignedTo === sub.name
            );

            const totalQuantity = assignedOrders.reduce((sum, o) => sum + (o.quantity || 0), 0);
            const completedQuantity = assignedOrders.reduce((sum, o) => sum + (o.completedQuantity || 0), 0);
            const totalCost = assignedOrders.reduce((sum, o) => {
                if (o.stages) {
                    return sum + o.stages.reduce((stageSum, stage) => {
                        if (stage.subcontractors) {
                            return stageSum + stage.subcontractors
                                .filter(s => s.supplierId === sub.id)
                                .reduce((subSum, s) => subSum + (s.laborCost || 0), 0);
                        }
                        return stageSum;
                    }, 0);
                }
                return sum;
            }, 0);

            return {
                id: sub.id,
                name: sub.name,
                orderCount: assignedOrders.length,
                totalQuantity,
                completedQuantity,
                progress: totalQuantity > 0 ? Math.round((completedQuantity / totalQuantity) * 100) : 0,
                totalCost
            };
        }).filter(sub => sub.orderCount > 0);

        return {
            subcontractors: subcontractorData,
            totals: {
                totalOrders: subcontractorData.reduce((sum, s) => sum + s.orderCount, 0),
                totalQuantity: subcontractorData.reduce((sum, s) => sum + s.totalQuantity, 0),
                totalCompleted: subcontractorData.reduce((sum, s) => sum + s.completedQuantity, 0),
                totalCost: subcontractorData.reduce((sum, s) => sum + s.totalCost, 0)
            }
        };
    }, [productionOrders, subcontractors]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <div>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                        <p>Fason üretim verileri yükleniyor...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <Link to="/uretim/raporlar" style={styles.backLink}>← Üretim Raporları</Link>
            </div>

            <div style={styles.box}>
                <h2 style={styles.title}>Fason Bazlı Üretim Raporu</h2>
                <p style={{ color: '#64748b', marginTop: '8px', marginBottom: '24px' }}>
                    Fasonculara verilen üretim emirlerinin durumu ve maliyetleri
                </p>

                <div style={styles.summaryCards}>
                    <div style={styles.summaryCard}>
                        <div style={styles.cardTitle}>Toplam Emir</div>
                        <div style={styles.cardValue}>{reportData.totals.totalOrders}</div>
                    </div>
                    <div style={styles.summaryCard}>
                        <div style={styles.cardTitle}>Toplam Miktar</div>
                        <div style={styles.cardValue}>{reportData.totals.totalQuantity} adet</div>
                    </div>
                    <div style={styles.summaryCard}>
                        <div style={styles.cardTitle}>Tamamlanan</div>
                        <div style={styles.cardValue}>{reportData.totals.totalCompleted} adet</div>
                    </div>
                    <div style={styles.summaryCard}>
                        <div style={styles.cardTitle}>Toplam Maliyet</div>
                        <div style={styles.cardValue}>{formatCurrency(reportData.totals.totalCost)}</div>
                    </div>
                </div>

                {reportData.subcontractors.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏭</div>
                        <h3 style={{ color: '#374151' }}>Fason Üretim Bulunamadı</h3>
                        <p>Fasonculara atanmış üretim emri bulunmuyor.</p>
                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Fasoncu</th>
                                <th style={styles.th}>Emir Sayısı</th>
                                <th style={styles.th}>Toplam Miktar</th>
                                <th style={styles.th}>Tamamlanan</th>
                                <th style={styles.th}>İlerleme</th>
                                <th style={styles.th}>Maliyet</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.subcontractors.map(sub => (
                                <tr key={sub.id}>
                                    <td style={{ ...styles.td, fontWeight: '600' }}>{sub.name}</td>
                                    <td style={styles.td}>{sub.orderCount}</td>
                                    <td style={styles.td}>{sub.totalQuantity} adet</td>
                                    <td style={styles.td}>{sub.completedQuantity} adet</td>
                                    <td style={styles.td}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ width: '60px', height: '6px', background: '#e2e8f0', borderRadius: '3px' }}>
                                                <div style={{ width: `${sub.progress}%`, height: '100%', background: '#10b981', borderRadius: '3px' }} />
                                            </div>
                                            <span>{sub.progress}%</span>
                                        </div>
                                    </td>
                                    <td style={styles.td}>{formatCurrency(sub.totalCost)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default SubcontractorProductionReport;
